import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";

const AccessContext = createContext({});

export function AccessProvider({ children }) {
  const [config, setConfig] = useState([]);
  const [ready, setReady] = useState(false);
  // 149-cw-r152: `error` state exposed supaya AppShell dapat render banner
  // "Access permission gagal dimuat" + tombol Retry. Tanpa ini, user kehilangan
  // semua sidebar item begitu network hiccup di boot (canAccess return false
  // karena config kosong) — user experience = "tiba-tiba tampilan kosong".
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);

  const reload = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    // 149-cw-r152: retry dgn exponential backoff (3 tries) supaya transient
    // network error tidak membuat access matrix kosong permanen.
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/access-controls");
        setConfig(data || []);
        setError(null);
        setReady(true);
        inFlightRef.current = false;
        return;
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        // Auth-related errors: user belum login / session invalid — jangan retry.
        if (status === 401 || status === 403) break;
        // Backoff: 400ms, 1200ms.
        if (attempt < 2) await new Promise((r) => setTimeout(r, 400 * (attempt + 1) * 3));
      }
    }
    // Semua percobaan gagal — simpan error supaya UI bisa render retry banner.
    // Jangan setConfig([]) supaya kalau ada cache dari session sebelumnya
    // (misal reload after focus), tetap pakai yg lama daripada kosong total.
    setError(lastErr || new Error("Failed to load access matrix"));
    setReady(true);
    inFlightRef.current = false;
    try { console.warn("[AccessProvider] Failed to load /access-controls after retries:", lastErr); } catch {}
  }, []);

  useEffect(() => {
    reload();
    // 149-cw-r152: auto-refetch pada window focus / online supaya user yang
    // sempat sleep laptop / kehilangan jaringan sebentar mendapat matrix fresh
    // saat kembali online — tanpa perlu hard reload.
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") reload();
    };
    const onOnline = () => reload();
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);

  // Return the effective permission cell for a (user, feature) with tri-state
  // override applied (16-AC-MATRIX-UI-R2): deny > allow > default(div/level).
  // Returns null when the matrix doesn't have an entry for that group.
  const cellFor = useCallback((user, featureKey) => {
    if (!user) return null;
    const entry = config.find((c) => c.feature === featureKey);
    if (!entry) return null;
    const div = (entry.perms || {})[user.division] || {};
    const base = div[String(user.level)] || null;
    if (!user.id) return base;
    const allowOverride = (entry.user_perms || {})[user.id] || null;
    const denyOverride = (entry.user_perms_deny || {})[user.id] || null;
    if (!allowOverride && !denyOverride) return base;
    const effective = { ...(base || {}) };
    if (allowOverride) {
      Object.entries(allowOverride).forEach(([k, v]) => { if (v) effective[k] = true; });
    }
    if (denyOverride) {
      Object.entries(denyOverride).forEach(([k, v]) => { if (v) effective[k] = false; });
    }
    return effective;
  }, [config]);

  // Granular permission check — perm ∈ ("active", "create", "update", "delete", "view").
  // Precedence:
  //   1. BOD Lv1 super-admin → always true (hardcoded failsafe, mirrors backend).
  //   2. access_overrides.{featureKey} → legacy flip: force-grant or force-revoke.
  //   3. Matrix cell → requires `active=true` gate, then the specific perm.
  const canPerm = useCallback((user, featureKey, perm) => {
    if (!user) return false;
    if (user.division === "BOD" && user.level === 1) return true;
    const overrides = user.access_overrides || {};
    if (Object.prototype.hasOwnProperty.call(overrides, featureKey)) {
      return !!overrides[featureKey];
    }
    const cell = cellFor(user, featureKey);
    if (!cell) return false;
    if (!cell.active) return false;
    if (perm === "active") return true;
    return !!cell[perm];
  }, [cellFor]);

  // Backward-compat: `canAccess` == `view` permission.
  const canAccess = useCallback((user, featureKey) => canPerm(user, featureKey, "view"), [canPerm]);

  // Visibility scope for scope-aware features:
  //   "own" | "division" | "division_lower" | "all". Defaults to "own" if not set.
  //   BOD Lv1 → always "all".
  const visibilityScope = useCallback((user, featureKey) => {
    if (!user) return "own";
    if (user.division === "BOD" && user.level === 1) return "all";
    const entry = config.find((c) => c.feature === featureKey);
    if (!entry) return "own";
    const vis = (entry.visibility || {})[user.division] || {};
    const s = vis[String(user.level)];
    return (["own", "division", "division_lower", "all"].includes(s)) ? s : "own";
  }, [config]);

  const value = useMemo(
    () => ({ config, canAccess, canPerm, visibilityScope, reload, ready, error }),
    [config, canAccess, canPerm, visibilityScope, reload, ready, error]
  );
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() { return useContext(AccessContext); }
